// Banner display + animations. Each banner has a rarity-tiered animation effect overlay.
import { motion } from "framer-motion";

export const RARITY_META = {
    common: { label: "COMMON", color: "#9ca3af" },
    rare: { label: "RARE", color: "#22d3ee" },
    epic: { label: "EPIC", color: "#a855f7" },
    legendary: { label: "LEGENDARY", color: "#facc15" },
    mythic: { label: "MYTHIC", color: "#ef4444" },
};

function PulseGreen({ color }) {
    return (
        <motion.span aria-hidden className="absolute inset-0 pointer-events-none"
            animate={{ boxShadow: [`inset 0 0 30px ${color}55`, `inset 0 0 60px ${color}99`, `inset 0 0 30px ${color}55`] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }} />
    );
}
function FlamesRed({ color }) {
    return (
        <>
            <motion.span aria-hidden className="absolute -bottom-2 left-0 right-0 h-12 pointer-events-none"
                style={{ background: `linear-gradient(0deg, ${color}, transparent)`, mixBlendMode: "screen" }}
                animate={{ scaleY: [0.85, 1.1, 0.85], opacity: [0.6, 0.9, 0.6] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} />
            <motion.span aria-hidden className="absolute inset-0 pointer-events-none"
                style={{ background: `radial-gradient(circle at 50% 100%, ${color}33, transparent 60%)` }} />
        </>
    );
}
function ThunderBlue({ color }) {
    return (
        <motion.span aria-hidden className="absolute inset-0 pointer-events-none"
            style={{ background: `linear-gradient(110deg, transparent 30%, ${color}66 50%, transparent 70%)`, backgroundSize: "200% 100%" }}
            animate={{ backgroundPosition: ["-100% 0%", "200% 0%"] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "linear" }} />
    );
}
function LavaOrange({ color }) {
    return (
        <>
            <motion.span aria-hidden className="absolute inset-0 pointer-events-none"
                style={{ background: `radial-gradient(ellipse at 30% 80%, ${color}55, transparent 60%), radial-gradient(ellipse at 80% 30%, #ef444455, transparent 60%)` }} />
            <motion.span aria-hidden className="absolute inset-x-0 bottom-0 h-2 pointer-events-none"
                style={{ background: `linear-gradient(90deg, ${color}, #ef4444, ${color})` }}
                animate={{ x: ["-50%", "50%", "-50%"], filter: ["blur(6px)", "blur(2px)", "blur(6px)"] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
        </>
    );
}
function ParticlesGold({ color }) {
    const ps = Array.from({ length: 14 }).map((_, i) => i);
    return (
        <span aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
            {ps.map((i) => (
                <motion.span key={i}
                    className="absolute rounded-full"
                    style={{ width: 4, height: 4, background: color, boxShadow: `0 0 8px ${color}` }}
                    initial={{ x: `${(i * 71) % 100}%`, y: "110%", opacity: 0.9 }}
                    animate={{ y: ["110%", "-10%"], opacity: [0.9, 0] }}
                    transition={{ duration: 4 + (i % 4), repeat: Infinity, delay: i * 0.25, ease: "easeOut" }}
                />
            ))}
        </span>
    );
}
function HolographicRainbow() {
    return (
        <motion.span aria-hidden className="absolute inset-0 pointer-events-none"
            style={{ background: "linear-gradient(120deg, rgba(34,211,238,0.35), rgba(168,85,247,0.35), rgba(250,204,21,0.35), rgba(239,68,68,0.35), rgba(34,211,238,0.35))", backgroundSize: "400% 400%", mixBlendMode: "overlay" }}
            animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
            transition={{ duration: 6, repeat: Infinity, ease: "linear" }} />
    );
}

export function BannerAnimationOverlay({ animation, color }) {
    switch (animation) {
        case "pulse_green": return <PulseGreen color={color} />;
        case "flames_red": return <FlamesRed color={color} />;
        case "thunder_blue": return <ThunderBlue color={color} />;
        case "lava_orange": return <LavaOrange color={color} />;
        case "particles_gold": return <ParticlesGold color={color} />;
        case "holographic_rainbow": return <HolographicRainbow />;
        default: return <PulseGreen color={color || "#a855f7"} />;
    }
}

// Display banner: image + animation overlay
export const BannerDisplay = ({ banner, height = 120, className = "" }) => {
    if (!banner) return null;
    return (
        <div className={`relative w-full overflow-hidden rounded-xl border border-[#242436] ${className}`} style={{ height }}>
            {banner.image ? (
                <img src={banner.image} alt={banner.name} className="absolute inset-0 w-full h-full object-cover" data-testid="banner-image" />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center" style={{ background: `linear-gradient(135deg, #14141c, ${banner.color || "#6f3fec"}33)` }}>
                    <span className="text-xs font-mono uppercase text-[#8b8ba0]">No image yet</span>
                </div>
            )}
            <BannerAnimationOverlay animation={banner.animation} color={banner.color} />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(10,10,15,0.55) 100%)" }} />
        </div>
    );
};
